import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import SupplierApiService from "@/core/services/api.service.supplier";

export default {
  name: "product-catalog",
  data() {
    return {
      // Filters
      filters: {
        categories: [],
        brand: "",
        vendor: "",
        fromCostPrice: null,
        toCostPrice: null,
        hasQuantity: false,
        // Category dropdown state
        dropdownOpen: false,
        searchText: ""
      },

      // Table data
      products: [],
      totalProducts: 0,
      loading: false,
      pagination: {
        page: 1,
        itemsPerPage: 25
      },

      originalProducts: [],

      // Service ready
      serviceReady: false,

      // Expanded rows for variations
      expanded: [],

      // Available filter options
      availableCategories: [],
      availableVendors: [],

      // Oobyte categories for filtering
      oobyteCategories: [],
      expandedCategories: new Set(),
      originalCategories: [],
      loadingCategories: false,
      categoryDropdownOpen: false,
      categorySearchText: "",

      // Validation rules
      rules: {
        required: value => !!value || "This field is required",
        priceGreaterThanZero: value =>
          (value && parseFloat(value) > 0) || "Price must be greater than 0",
        salePriceValidation: originalPrice => value => {
          if (!value) return true; // Sale price is optional
          const salePrice = parseFloat(value);
          const price = parseFloat(originalPrice);
          return salePrice <= price || "Sale price cannot be greater than regular price";
        },
        variationSalePriceValidation: variationPrice => value => {
          if (!value) return true; // Sale price is optional
          const salePrice = parseFloat(value);
          const price = parseFloat(variationPrice);
          return salePrice <= price || "Sale price cannot be greater than regular price";
        }
      }
    };
  },

  computed: {
    headers() {
      return [
        { text: "", value: "data-table-expand" },
        { text: "Published", value: "isPublished", sortable: false, width: "120px" },
        { text: "Image", value: "image", sortable: false, width: "80px" },
        { text: "Name", value: "name", sortable: false },
        { text: "Price", value: "price", sortable: false, width: "100px" },
        { text: "Sale Price", value: "salePrice", sortable: false, width: "120px" },
        { text: "SKU", value: "sku", sortable: false, width: "120px" },
        { text: "EAN", value: "ean", sortable: false, width: "120px" },
        { text: "Quantity", value: "quantity", sortable: false, width: "100px" },
        { text: "Cost Price", value: "costPrice", sortable: false, width: "120px" },
        { text: "Brand", value: "brand", sortable: false, width: "120px" },
        { text: "Vendor", value: "source", sortable: false, width: "100px" },
        { text: "Last Sync", value: "lastSync", sortable: false, width: "140px" }
      ];
    },

    variationHeaders() {
      return [
        { text: "Type", value: "type", sortable: false },
        { text: "Value", value: "value", sortable: false },
        { text: "Price", value: "price", sortable: false, width: "100px" },
        { text: "Sale Price", value: "salePrice", sortable: false, width: "120px" },
        { text: "SKU", value: "sku", sortable: false, width: "120px" },
        { text: "EAN", value: "ean", sortable: false, width: "120px" },
        { text: "Quantity", value: "quantity", sortable: false, width: "100px" },
        { text: "Last Sync", value: "lastSync", sortable: false, width: "140px" }
      ];
    }
  },

  mounted() {
    // Initialize API service
    SupplierApiService.init().then(() => {
      this.serviceReady = true;
      // Load initial data
      this.fetchProductFilters();
      this.fetchOobyteCategories();
      this.fetchProducts();
    });

    // Add event listener for outside click
    document.addEventListener("click", this.handleOutsideClick);
  },

  beforeDestroy() {
    // Remove event listener for outside click
    document.removeEventListener("click", this.handleOutsideClick);
  },

  watch: {
    pagination: {
      handler() {
        if (this.serviceReady) {
          this.fetchProducts();
        }
      },
      deep: true
    }
  },

  async created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Dashboard", route: "dashboard" },
      { title: "Product Catalog" }
    ]);
  },

  methods: {
    // Fetch products from real API
    async fetchProducts() {
      this.loading = true;

      try {
        const params = {
          page: this.pagination.page,
          maxResults: this.pagination.itemsPerPage,
          categories: this.filters.categories.length > 0 ? this.filters.categories : null,
          brand: this.filters.brand,
          source: this.filters.vendor,
          fromCostPrice: this.filters.fromCostPrice,
          toCostPrice: this.filters.toCostPrice,
          hasQuantity: this.filters.hasQuantity
        };

        const response = await SupplierApiService.getSupplierProducts(params);

        // Handle API response structure
        if (response.data) {
          this.products = response.data.products.results || [];
          this.totalProducts = response.data.products.metadata?.totalCount || 0;

          // Deep copy to ensure originalProducts is immutable
          this.originalProducts = JSON.parse(JSON.stringify(this.products));
        } else {
          this.products = [];
          this.totalProducts = 0;

          this.originalProducts = [];
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        this.$toast?.error("Failed to fetch products");

        // Fallback to empty state on error
        this.products = [];
        this.totalProducts = 0;

        this.originalProducts = [];
      } finally {
        this.loading = false;
      }
    },

    // Fetch product filters from real API
    async fetchProductFilters() {
      try {
        const response = await SupplierApiService.getProductFilters();

        if (response.data && response.data.categories) {
          this.availableCategories = response.data.categories.map(cat => ({
            id: cat.id,
            name: cat.name
          }));
        }
        if (response.data && response.data.vendors) {
          this.availableVendors = response.data.vendors;
        }
      } catch (error) {
        console.error("Error fetching product filters:", error);
      }
    },

    // Fetch Oobyte categories from real API
    async fetchOobyteCategories() {
      this.loadingCategories = true;

      try {
        const response = await SupplierApiService.getOobyteCategories();

        if (response.data && Array.isArray(response.data)) {
          // Flatten categories and subcategories into a single searchable array
          this.oobyteCategories = this.flattenCategories(response.data);
          this.storeOriginalCategories(response.data);
        } else {
          console.error("Invalid response format for Oobyte categories");
          this.oobyteCategories = [];
        }
      } catch (error) {
        console.error("Error fetching Oobyte categories:", error);
        this.oobyteCategories = [];
      } finally {
        this.loadingCategories = false;
      }
    },

    // Flatten categories into hierarchical structure for dropdown
    flattenCategories(categories, isFullFlatten = false) {
      const flattened = [];

      categories.forEach(category => {
        // Level 0 - Main category (not selectable)
        flattened.push({
          id: `header_${category.id}`,
          title: category.title,
          displayName: category.title,
          level: 0,
          selectable: false,
          originalId: category.id
        });

        // Level 1 - Subcategories
        if (category.subcategories && category.subcategories.length > 0) {
          category.subcategories.forEach(subcategory => {
            const hasSubSubcategories =
              subcategory.subcategories && subcategory.subcategories.length > 0;
            const isExpanded = this.expandedCategories.has(subcategory.id);

            flattened.push({
              id: subcategory.id,
              title: subcategory.title,
              displayName: subcategory.title,
              level: 1,
              selectable: true, // Always selectable now
              parentId: category.id,
              parentTitle: category.title,
              originalId: subcategory.id,
              hasSubcategories: hasSubSubcategories,
              expanded: isExpanded
            });

            // Level 2 - Sub-subcategories (always selectable, only show if parent is expanded)
            if ((hasSubSubcategories && isExpanded) || isFullFlatten) {
              subcategory.subcategories.forEach(subSubcategory => {
                flattened.push({
                  id: subSubcategory.id,
                  title: subSubcategory.title,
                  displayName: subSubcategory.title,
                  level: 2,
                  selectable: true,
                  parentId: subcategory.id,
                  parentTitle: subcategory.title,
                  grandParentId: category.id,
                  grandParentTitle: category.title,
                  originalId: subSubcategory.id
                });
              });
            }
          });
        }
      });

      return flattened;
    },

    // Get hierarchical categories for filters with search support
    getHierarchicalCategoriesForFilters() {
      if (!this.originalCategories || this.originalCategories.length === 0) {
        return [];
      }

      const searchText = this.filters.searchText || "";

      if (!searchText.trim()) {
        // Clear all expanded categories when search is cleared to show only level 1 categories
        // this.expandedCategories.clear();
        // Return normal flattened categories (all collapsed)
        return this.flattenCategories(this.originalCategories);
      }

      // For search, get all categories including nested ones (full flatten)
      const allCategories = this.flattenCategories(this.originalCategories, true);

      // Filter categories based on search text
      const searchLower = searchText.toLowerCase();
      const filteredCategories = [];
      const addedHeaders = new Set();

      allCategories.forEach(category => {
        if (category.level === 0) {
          // Always check if we need this header
          return;
        }

        // Check if category matches search
        const matches = category.title.toLowerCase().includes(searchLower);

        if (matches) {
          // Add parent header if not already added
          const headerKey =
            category.level === 1
              ? `header_${category.parentId}`
              : `header_${category.grandParentId}`;

          if (!addedHeaders.has(headerKey)) {
            const headerCategory = allCategories.find(cat => cat.id === headerKey);
            if (headerCategory) {
              filteredCategories.push(headerCategory);
              addedHeaders.add(headerKey);
            }
          }

          // Add intermediate parent for level 2 items and expand it
          if (category.level === 2) {
            const parentKey = category.parentId;
            const parentExists = filteredCategories.some(cat => cat.id === parentKey);
            if (!parentExists) {
              const parentCategory = allCategories.find(cat => cat.id === parentKey);
              if (parentCategory) {
                // Expand the parent when showing search results
                parentCategory.expanded = true;
                this.expandedCategories.add(parentCategory.id);
                filteredCategories.push(parentCategory);
              }
            }
          }

          filteredCategories.push(category);
        }
      });

      return filteredCategories;
    },

    // Get display name for selected categories in filters (multiselect)
    getSelectedCategoryDisplayNameForFilters() {
      if (!this.filters.categories || this.filters.categories.length === 0) return "";

      if (this.filters.categories.length === 1) {
        const selectedCategory = this.flattenCategories(this.originalCategories || [], true).find(
          cat => cat.id === this.filters.categories[0]
        );
        return selectedCategory ? selectedCategory.title : "";
      }

      return `${this.filters.categories.length} categories selected`;
    },

    // Toggle dropdown for filters
    toggleDropdownForFilters() {
      this.filters.dropdownOpen = !this.filters.dropdownOpen;
    },

    // Clear selection for filters
    clearSelectionForFilters() {
      this.filters.categories = [];
    },

    // Handle custom dropdown click for filters (multiselect with checkboxes)
    handleCustomDropdownClickForFilters(categoryItem) {
      if (!categoryItem.selectable) return;

      const categoryId = categoryItem.id;
      const currentIndex = this.filters.categories.indexOf(categoryId);

      if (currentIndex > -1) {
        // Remove if already selected
        this.filters.categories.splice(currentIndex, 1);
      } else {
        // Add if not selected
        this.filters.categories.push(categoryId);
      }
    },

    // Check if category is selected (for checkbox state)
    isCategorySelected(categoryId) {
      return this.filters.categories.includes(categoryId);
    },

    // Toggle category expansion
    toggleCategoryExpansion(categoryId) {
      if (this.expandedCategories.has(categoryId)) {
        this.expandedCategories.delete(categoryId);
      } else {
        this.expandedCategories.add(categoryId);
      }

      // Force update to refresh all dropdowns
      this.$forceUpdate();
    },

    // Store original categories for re-flattening
    storeOriginalCategories(categories) {
      this.originalCategories = categories;
    },

    // Apply filters and refresh data
    applyFilters() {
      this.pagination.page = 1;
      this.fetchProducts();
    },

    // Clear all filters
    clearFilters() {
      this.filters = {
        categories: [],
        brand: "",
        vendor: "",
        fromCostPrice: null,
        toCostPrice: null,
        hasQuantity: false,
        dropdownOpen: false,
        searchText: ""
      };
      this.pagination.page = 1;
      this.fetchProducts();
    },

    // Validate and update main product field
    validateAndUpdateProduct(product, field) {
      const value = product[field];

      // Validate based on field type
      let isValid = true;
      let errorMessage = "";

      switch (field) {
        case "name":
          if (!value || value.trim() === "") {
            isValid = false;
            errorMessage = "Name cannot be empty";
          }
          break;
        case "price":
          if (!value || parseFloat(value) <= 0) {
            isValid = false;
            errorMessage = "Price must be greater than 0";
          }
          break;
        case "salePrice":
          if (value && parseFloat(value) > parseFloat(product.price)) {
            isValid = false;
            errorMessage = "Sale price cannot be greater than regular price";
          }
          break;
      }

      if (isValid) {
        this.updateProduct(product);
      } else {
        // Show error and revert to original value
        console.error(errorMessage);
        this.cancelEditProduct(product, field);
      }
    },

    // Cancel edit and restore original value
    cancelEditProduct(product, field) {
      const originalProduct = this.originalProducts.find(p => p.id === product.id);
      if (originalProduct) {
        const originalValue = originalProduct[field];

        setTimeout(() => {
          this.$set(product, field, originalValue);
        }, 0);
      }
    },

    // Validate and update variation field
    validateAndUpdateVariation(product, variation, field) {
      const value = variation[field];

      // Validate based on field type
      let isValid = true;
      let errorMessage = "";

      switch (field) {
        case "type":
        case "value":
          if (!value || value.trim() === "") {
            isValid = false;
            errorMessage = `${field.charAt(0).toUpperCase() + field.slice(1)} cannot be empty`;
          }
          break;
        case "price":
          if (!value || parseFloat(value) <= 0) {
            isValid = false;
            errorMessage = "Price must be greater than 0";
          }
          break;
        case "salePrice":
          if (value && parseFloat(value) > parseFloat(variation.price)) {
            isValid = false;
            errorMessage = "Sale price cannot be greater than regular price";
          }
          break;
      }

      if (isValid) {
        this.updateProduct(product, variation);
      } else {
        // Show error and revert to original value
        console.error(errorMessage);
        this.cancelVariationEdit(product, variation, field);
      }
    },

    // Cancel variation edit and restore original value
    cancelVariationEdit(product, variation, field) {
      const originalProduct = this.originalProducts.find(p => p.id === product.id);
      const originalVariation = originalProduct.variations.find(v => v.id === variation.id);
      if (originalProduct && originalVariation) {
        const originalValue = originalVariation[field];

        setTimeout(() => {
          this.$set(variation, field, originalValue);
        }, 0);
      }
    },

    // Update product using real API
    async updateProduct(product, variation = null) {
      try {
        const productData = {
          id: product.id,
          name: product.name,
          price: +product.price,
          salePrice: +product.salePrice
        };

        if (variation) {
          const variationData = {
            id: variation.id,
            type: variation.type,
            value: variation.value,
            price: +variation.price,
            salePrice: variation.salePrice ? +variation.salePrice : null
          };
          productData.variations = [variationData];
        }

        await SupplierApiService.updateSupplierProduct(productData);

        const title = variation ? "Variation updated successfully" : "Product updated successfully";
        this.$bvToast?.toast(title, {
          variant: "success",
          solid: true,
          autoHideDelay: 2000
        });

        // Optionally refresh the data to get latest state
        await this.fetchProducts();
      } catch (error) {
        console.error("Error updating product:", error);
        const title = variation ? "Variation updated failed" : "Product updated failed";
        this.$bvToast?.toast(title, {
          variant: "danger",
          solid: true,
          autoHideDelay: 2000
        });

        // Optionally revert the changes in UI
        await this.fetchProducts();
      }
    },

    // Publish product
    async publishProduct(product) {
      try {
        await SupplierApiService.publishProduct(product.id);
        this.$bvToast?.toast("Product publishing successfully initiated", {
          variant: "success",
          solid: true,
          autoHideDelay: 2000
        });
        // Optionally refresh the data to get latest state
        await this.fetchProducts();
      } catch (error) {
        console.error("Error publishing product:", error);
        this.$bvToast?.toast("Product published failed", {
          variant: "danger",
          solid: true,
          autoHideDelay: 2000
        });
        // Optionally revert the changes in UI
        await this.fetchProducts();
      }
    },

    // Handle outside click for category dropdown
    handleOutsideClick(event) {
      const target = event.target;

      // Check if click is on the category dropdown or its children
      const isDropdownClick =
        target.closest(".custom-dropdown-menu") ||
        target.closest(".custom-select-wrapper") ||
        target.closest(".v-text-field");

      // If click is inside dropdown area, return early
      if (isDropdownClick) {
        return;
      }

      // Close the category filter dropdown if it's open
      if (this.filters.dropdownOpen) {
        this.filters.dropdownOpen = false;
      }
    }
  }
};
